import { graphql, Link } from "gatsby";
import { ClickableAction } from "@unum/clickable";
import { LinkFragmentFragment } from "../../graphql-types";

export const getClickableAction = (
  data: LinkFragmentFragment
): ClickableAction => {
  if (!data) {
    return null;
  }

  if (data.__typename === "kontent_item_promo") {
    return getClickableAction(data.elements.cta.value[0]);
  }

  if (data.__typename === "kontent_item_external_link") {
    return {
      model: "htmlLink",
      href: data.elements.url.value,
      target: "_blank"
    };
  }

  if (data.__typename === "kontent_item_internal_link") {
    const page = data.elements.page.value[0];

    if (!page) {
      return;
    }

    if (page.__typename === "kontent_item_home_page") {
      return {
        model: "routerLink",
        to: "/",
        linkComponent: Link
      };
    }

    if (
      !(
        page.__typename === "kontent_item_page" ||
        page.__typename === "kontent_item_article" ||
        page.__typename === "kontent_item_product" ||
        page.__typename === "kontent_item_branch" ||
        page.__typename === "kontent_item_dynamic_page" ||
        page.__typename === "kontent_item_wellbeing_hub_home_page"
      ) ||
      !page?.elements.path.value
    ) {
      return;
    }

    return {
      model: "routerLink",
      to: page.elements.path.value,
      linkComponent: Link
    };
  }

  if (
    data.__typename === "kontent_item_page" ||
    data.__typename === "kontent_item_article" ||
    data.__typename === "kontent_item_product" ||
    data.__typename === "kontent_item_branch" ||
    data.__typename === "kontent_item_dynamic_page" ||
    data.__typename === "kontent_item_wellbeing_hub_home_page"
  ) {
    return {
      model: "routerLink",
      to: data.elements.path.value,
      linkComponent: Link
    };
  }
};

export const resolveLinkHref = (data: LinkFragmentFragment): string | null => {
  if (!data) {
    return null;
  }

  const action = getClickableAction(data);
  const model = action?.model;

  if (
    (model !== "htmlLink" && model !== "routerLink") ||
    (data.__typename !== "kontent_item_external_link" &&
      data.__typename !== "kontent_item_internal_link")
  ) {
    return null;
  }

  return model === "htmlLink" ? action.href : action.to;
};

export const resolveLinkContent = (
  data: LinkFragmentFragment
): string | null => {
  if (!data) {
    return null;
  }

  if (
    data.__typename !== "kontent_item_external_link" &&
    data.__typename !== "kontent_item_internal_link"
  ) {
    return null;
  }

  return data.elements.label.value;
};

export const query = graphql`
  fragment CTALinkFragment on kontent_item {
    __typename
    ... on kontent_item_external_link {
      elements {
        url {
          value
        }
        label {
          value
        }
      }
    }

    ... on kontent_item_internal_link {
      elements {
        label {
          value
        }
        # @todo: Page, based on page union.
        page {
          value {
            __typename
            ... on kontent_item_page {
              elements {
                path {
                  value
                }
              }
            }
            ... on kontent_item_article {
              elements {
                path {
                  value
                }
              }
            }
            ... on kontent_item_product {
              elements {
                path {
                  value
                }
              }
            }
            ... on kontent_item_branch {
              elements {
                path {
                  value
                }
              }
            }
            ... on kontent_item_dynamic_page {
              elements {
                path {
                  value
                }
              }
            }
            ... on kontent_item_wellbeing_hub_home_page {
              elements {
                path {
                  value
                }
              }
            }
          }
        }
      }
    }
  }

  fragment LinkFragment on kontent_item {
    ...CTALinkFragment

    ... on kontent_item_promo {
      elements {
        cta {
          value {
            ...CTALinkFragment
          }
        }
      }
    }
    ... on kontent_item_page {
      elements {
        path {
          value
        }
        title {
          value
        }
      }
    }
    ... on kontent_item_article {
      elements {
        path {
          value
        }
        title {
          value
        }
      }
    }
    ... on kontent_item_product {
      elements {
        path {
          value
        }
        title {
          value
        }
      }
    }
    ... on kontent_item_branch {
      elements {
        path {
          value
        }
        title {
          value
        }
      }
    }
    ... on kontent_item_dynamic_page {
      elements {
        path {
          value
        }
        title {
          value
        }
      }
    }
    ... on kontent_item_wellbeing_hub_home_page {
      elements {
        path {
          value
        }
        title {
          value
        }
      }
    }
  }
`;
